import { ReactNode, useEffect, useState } from 'react';
import { IntercomProvider, useIntercom } from 'react-use-intercom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { FacebookProvider } from 'react-facebook';
import { ToastContainer } from 'react-toastify';
import { useRouter } from 'next/router';

import { ReactQueryWrapper, ServiceProvider } from '@restworld/data-services';
import { AuthProvider, readStoredToken, useAuth } from '@restworld/feature-authentication';

import { GlobalOptionsFetchingProvider, UTMSourceProvider, ToastMessageContextProvider, AnalyticsProvider } from './';
import { IubendaProvider } from './iubenda';
import { defaultToastConfig } from '../constants/default-toast-config';
import { useIsEmployerApp } from '../hooks';
import { useIntercomBootProps } from '../hooks/use-intercom-boot-props';

const baseUrl = process.env.NEXT_PUBLIC_BASE_API_URL ?? '';
const INTERCOM_APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID ?? '';
const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? '';
const facebookAppId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID ?? '';

// GoogleOAuthProvider and FacebookProvider each download their SDK on mount, so an app with no
// social login (the employer app) opts out instead of paying for them on every page.
const SocialLoginProviders = ({ enabled, children }: { enabled: boolean; children: ReactNode }) =>
  enabled ? (
    <GoogleOAuthProvider clientId={googleClientId}>
      <FacebookProvider appId={facebookAppId}>{children}</FacebookProvider>
    </GoogleOAuthProvider>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{children}</>
  );

export const MainContext = ({
  children,
  withSocialLogin = true,
}: {
  children: React.ReactNode;
  withSocialLogin?: boolean;
}) => (
  <ReactQueryWrapper>
    <AuthProvider baseUrl={baseUrl}>
      <ServiceProviderWrapper>
        <IubendaProvider>
          <SocialLoginProviders enabled={withSocialLogin}>
            <IntercomServiceProvider>
              <AnalyticsProvider>
                <UTMSourceProvider>
                  <GlobalOptionsFetchingProvider>
                    <ToastMessageContextProvider>{children}</ToastMessageContextProvider>
                    <ToastContainer {...defaultToastConfig} />
                  </GlobalOptionsFetchingProvider>
                </UTMSourceProvider>
              </AnalyticsProvider>
            </IntercomServiceProvider>
          </SocialLoginProviders>
        </IubendaProvider>
      </ServiceProviderWrapper>
    </AuthProvider>
  </ReactQueryWrapper>
);
const FIRST_INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;
// Safari has no requestIdleCallback: wait this long after `load` instead.
const IDLE_FALLBACK_DELAY_MS = 2000;

/**
 * True from the first tap or key press, or once the browser is idle after `load`, whichever
 * comes first. The Intercom widget is ~320 KB of script, and loading it 500 ms after mount put
 * it on the main thread while the first screen was still becoming usable.
 *
 * Nothing is lost while it is false: `boot` re-runs when the provider flips (its identity
 * changes), and every `show` / `showMessages` / `startTour` call site is a click, whose
 * pointerdown or keydown has already flipped the flag and booted the widget's queue by then.
 */
const useBrowserIdleOrFirstInteraction = () => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (isReady) return undefined;
    const markReady = () => setIsReady(true);
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const scheduleIdle = () => {
      if (typeof window.requestIdleCallback === 'function') idleId = window.requestIdleCallback(markReady);
      else timeoutId = setTimeout(markReady, IDLE_FALLBACK_DELAY_MS);
    };

    FIRST_INTERACTION_EVENTS.forEach((type) =>
      window.addEventListener(type, markReady, { once: true, passive: true, capture: true })
    );
    if (document.readyState === 'complete') scheduleIdle();
    else window.addEventListener('load', scheduleIdle, { once: true });

    return () => {
      FIRST_INTERACTION_EVENTS.forEach((type) => window.removeEventListener(type, markReady, { capture: true }));
      window.removeEventListener('load', scheduleIdle);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [isReady]);

  return isReady;
};

export const IntercomServiceProvider = ({ children }: { children: ReactNode }) => {
  const shouldInitialize = useBrowserIdleOrFirstInteraction();
  return (
    <IntercomProvider appId={INTERCOM_APP_ID} shouldInitialize={shouldInitialize}>
      <IntercomInitiation isInitialized={shouldInitialize}>{children}</IntercomInitiation>
    </IntercomProvider>
  );
};

const IntercomInitiation = ({ children, isInitialized }: { children?: ReactNode; isInitialized: boolean }) => {
  const { boot, shutdown } = useIntercom();
  const { user } = useAuth();
  const router = useRouter();
  const { isEmployerApp } = useIsEmployerApp();
  // The token travels inside `customAttributes` because react-use-intercom v5 predates
  // `intercom_user_jwt` and silently drops any boot prop it does not recognise, while
  // `customAttributes` is spread verbatim into the raw Intercom settings. See the helper for why
  // the id and the email are sent either inside the token or beside it, never both, and the hook
  // for why this value must not change identity on every navigation.
  const autoBootProps = useIntercomBootProps(user, isEmployerApp, router.pathname);

  useEffect(() => {
    // Booting before the provider initializes only logs a warning; the effect runs again
    // once it does.
    if (!isInitialized) return undefined;
    if (typeof window !== 'undefined') boot({ ...autoBootProps });
    return () => {
      shutdown();
    };
  }, [autoBootProps, boot, shutdown, isInitialized]);
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
export const ServiceProviderWrapper = ({ children }: WrapperProps) => {
  const { jwt: token } = useAuth();
  // Two defects lived on the line this replaces, both fixed by going through `readStoredToken`:
  //
  // 1. `token || typeof localStorage !== 'undefined' ? … : ''` parses as
  //    `(token || typeof localStorage !== 'undefined') ? … : ''` — `?:` binds looser than `||`. So
  //    `token` was only ever a truthiness input and never the value: a signed-in user's in-memory
  //    token was discarded and storage re-read on every render.
  // 2. `typeof localStorage !== 'undefined'` cannot see a **null** `localStorage`, because
  //    `typeof null === 'object'`. An Android WebView with site data disabled exposes exactly that,
  //    so the guard passed and `.getItem` threw on null — crashing `ServiceProviderWrapper`, which
  //    wraps the whole app, so the entire page went to the ErrorBoundary (prod, 02/10/2026).
  //
  // `readStoredToken` already handles both the missing-global (SSR) and unavailable-storage cases.
  const jwt = token || readStoredToken() || '';
  return (
    <ServiceProvider baseUrl={baseUrl} jwt={jwt}>
      {children}
    </ServiceProvider>
  );
};
interface WrapperProps {
  children: ReactNode;
}
